import { useEffect, useState } from 'react';
import {
  SEES_CLIENTS_NO,
  SEES_CLIENTS_QUESTION,
  SEES_CLIENTS_YES,
  type ContactInfo,
} from '@ehr/shared';
import { api } from '../lib/api.js';
import { Dd, type DdOption } from '../components/Dropdown.js';
import { IconX, color, msg, roleBody, roleOptions, type Team } from './usersShared.js';
import './usersModal.css';

/**
 * The V38 user profile window (renderUserProfile, "Profile & contact information"), used as
 * step 1 of the Add User wizard: Profile → Hours → Rights (ehr#60). An existing user's
 * pencil opens the Provider Profile instead (per Taras on ehr#60). Next creates the user.
 * ehr#232: it asks "Will this person see clients?" (providers vs support staff). Nothing is
 * picked to start with, and Next waits for an answer (Chase, 10-05).
 */

const US_STATES =
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
    ' ',
  );
const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];
const deviceZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
};

export interface ProfileResult {
  id: string;
  name: string;
  rightsMode: 'role' | 'custom';
}

export function UserProfileWindow({
  team,
  step,
  onClose,
  onNext,
}: {
  team: Team;
  step: string;
  onClose: () => void;
  onNext: (r: ProfileResult) => void;
}) {
  const [role, setRole] = useState('sys:clinician');
  // ehr#232 - null until picked: then the role decides (clinicians and supervisors see clients).
  const [seesPick, setSeesPick] = useState<boolean | null>(null);
  const [mode, setMode] = useState<'role' | 'custom'>('role');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [info, setInfo] = useState<ContactInfo>({
    phone: '',
    timezone: 'device',
    locationId: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [locations, setLocations] = useState<DdOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<{ id: string; name: string }[]>('/practice/locations')
      .then((ls) => {
        const opts = (Array.isArray(ls) ? ls : []).map((l) => ({ value: l.id, label: l.name }));
        setLocations(opts);
        setInfo((i) => (i.locationId || !opts[0] ? i : { ...i, locationId: opts[0].value }));
      })
      .catch(() => undefined);
  }, []);

  const set = (k: keyof ContactInfo, v: string) => setInfo({ ...info, [k]: v });
  const name = [first, last].filter((x) => x.trim()).join(' ') || 'New User';
  const roleLabel = roleOptions(team).find((o) => o.value === role)?.label ?? '';
  const titleOpts: DdOption[] = [
    { value: '', label: '— No title —' },
    ...team.titles.map((t) => ({ value: t.label, label: t.label })),
  ];
  const zoneOpts: DdOption[] = [
    { value: 'device', label: `Device time zone (${deviceZone() || 'this device'})` },
    ...ZONES.map((z) => ({ value: z, label: z.replace('_', ' ') })),
  ];

  const seesClients = seesPick;
  const next = () => {
    const r = roleBody(role, team);
    if (!first.trim() || !last.trim()) return setErr('First and last name are required.');
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setErr('Enter a valid email.');
    if (!r) return setErr('Pick a role.');
    if (seesClients === null) return setErr(`Answer "${SEES_CLIENTS_QUESTION}"`);
    setSaving(true);
    setErr(null);
    api
      .post<{ id: string }>('/settings/users', {
        email: email.trim(),
        firstName: first.trim(),
        lastName: last.trim(),
        title: title || null,
        contactInfo: info,
        seesClients,
        ...r,
      })
      .then((res) => {
        const id = res?.id;
        if (!id) throw new Error('The user was not created.');
        const done = () => onNext({ id, name, rightsMode: mode });
        // Custom rights: start the user on custom mode so step 3 edits their own overrides.
        return mode === 'custom'
          ? api.put(`/settings/users/${id}/rights`, { mode: 'custom', overrides: {} }).then(done)
          : done();
      })
      .catch((e: unknown) => setErr(msg(e)))
      .finally(() => setSaving(false));
  };

  return (
    <div className="um-fmap-ov" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="up-dialog" role="dialog" aria-modal="true" aria-label="User profile">
        <div className="up-head">
          <span className="up-avatar" style={{ background: color(email || name) }}>
            {(first[0] ?? 'N').toUpperCase()}
            {(last[0] ?? 'U').toUpperCase()}
          </span>
          <div className="up-head-main">
            <div className="up-name">{name}</div>
            <div className="up-sub">
              <span className="um-role-pill">
                <span>Role</span>
                {roleLabel}
              </span>
              Profile &amp; contact information
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            {IconX}
          </button>
        </div>
        <div className="up-body">
          <div className="up-label">Role</div>
          <Dd
            floating
            searchable={false}
            value={role}
            onChange={setRole}
            ariaLabel="Role"
            options={roleOptions(team)}
          />
          <div className="up-label">Access rights mode</div>
          <div className="up-seg" role="group" aria-label="Access rights mode">
            <button className={mode === 'role' ? 'on' : ''} onClick={() => setMode('role')}>
              Role rights
            </button>
            <button className={mode === 'custom' ? 'on' : ''} onClick={() => setMode('custom')}>
              Custom rights
            </button>
          </div>
          <div className="up-hint">
            {mode === 'role'
              ? 'This user inherits all permissions from their role. You can still change single rights in step 3.'
              : 'Set this user’s rights one by one in step 3; anything you leave follows their role.'}
          </div>
          <div className="up-divider" />
          <div className="up-grid">
            <label className="up-field">
              <span className="up-label">First name</span>
              <input value={first} autoFocus onChange={(e) => setFirst(e.target.value)} />
            </label>
            <label className="up-field">
              <span className="up-label">Last name</span>
              <input value={last} onChange={(e) => setLast(e.target.value)} />
            </label>
            <div className="up-field">
              <span className="up-label">Title</span>
              <Dd
                floating
                value={title}
                onChange={setTitle}
                ariaLabel="Title"
                options={titleOpts}
              />
            </div>
            <label className="up-field">
              <span className="up-label">Email</span>
              <input
                type="email"
                value={email}
                placeholder="name@practice.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="up-field">
              <span className="up-label">Phone</span>
              <input value={info.phone} onChange={(e) => set('phone', e.target.value)} />
            </label>
            <div className="up-field">
              <span className="up-label">
                Time zone <span className="up-label-soft">— how due times are shown</span>
              </span>
              <Dd
                floating
                searchable={false}
                value={info.timezone}
                onChange={(v) => set('timezone', v)}
                ariaLabel="Time zone"
                options={zoneOpts}
              />
            </div>
            <div className="up-field">
              <span className="up-label">Location</span>
              <Dd
                floating
                value={info.locationId}
                onChange={(v) => set('locationId', v)}
                placeholder={locations.length ? '— Select —' : 'No locations yet'}
                ariaLabel="Location"
                options={locations}
              />
            </div>
            {/* ehr#232 - two plain choices: the support-staff answer is too long for a dropdown. */}
            <div
              className="up-field up-sees"
              role="radiogroup"
              aria-label={SEES_CLIENTS_QUESTION}
              aria-required="true"
            >
              <span className="up-label">{SEES_CLIENTS_QUESTION}</span>
              {(
                [
                  [true, SEES_CLIENTS_YES],
                  [false, SEES_CLIENTS_NO],
                ] as const
              ).map(([v, label]) => (
                <label key={String(v)} className={`up-sees-opt${seesClients === v ? ' on' : ''}`}>
                  <input
                    type="radio"
                    name="up-sees-clients"
                    checked={seesClients === v}
                    onChange={() => setSeesPick(v)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <label className="up-field up-wide">
              <span className="up-label">Street address</span>
              <input value={info.line1} onChange={(e) => set('line1', e.target.value)} />
            </label>
            <label className="up-field up-wide">
              <span className="up-label">
                Address line 2 <span className="up-label-soft">— optional</span>
              </span>
              <input
                value={info.line2}
                placeholder="Apt, suite, unit"
                onChange={(e) => set('line2', e.target.value)}
              />
            </label>
            <label className="up-field">
              <span className="up-label">City</span>
              <input value={info.city} onChange={(e) => set('city', e.target.value)} />
            </label>
            <div className="up-field up-state-zip">
              <div>
                <span className="up-label">State</span>
                <Dd
                  floating
                  value={info.state}
                  onChange={(v) => set('state', v)}
                  ariaLabel="State"
                  options={US_STATES.map((s) => ({ value: s, label: s }))}
                />
              </div>
              <label>
                <span className="up-label">ZIP</span>
                <input
                  value={info.zip}
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(e) => set('zip', e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="up-foot">
          <span className="up-step">{step}</span>
          {/* ehr#203: the error sits by the button that raised it, not at the top of a
              scrolled-away body. */}
          {err && (
            <span className="foot-err" role="alert">
              {err}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" disabled={saving} onClick={next}>
            {saving ? 'Saving…' : 'Next — Hours ›'}
          </button>
        </div>
      </div>
    </div>
  );
}
